import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel,
  MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RefreshIcon from '@mui/icons-material/Refresh';

function authHeaders() {
  const t = sessionStorage.getItem('sessionToken');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'candidate', label: 'Candidate' },
  { value: 'ready', label: 'Ready' },
  { value: 'published', label: 'Published' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'archived', label: 'Archived' },
  { value: 'deleted', label: 'Deleted' },
];

const STATUS_COLOR = {
  candidate: 'default',
  ready: 'info',
  published: 'success',
  hidden: 'warning',
  archived: 'default',
  deleted: 'error',
};

function fmtDate(value) {
  if (!value) return '—';
  try { return new Date(value).toLocaleString(); } catch { return String(value); }
}

function fmtTags(arr) {
  if (!Array.isArray(arr) || !arr.length) return '—';
  return arr.slice(0, 4).join(', ') + (arr.length > 4 ? ` +${arr.length - 4}` : '');
}

function FanStoriesAdmin() {
  const [stories, setStories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Edit modal
  const [editStory, setEditStory] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editAdminHidden, setEditAdminHidden] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Detail modal
  const [detailStory, setDetailStory] = useState(null);
  const [detailPosts, setDetailPosts] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (from) p.set('from', new Date(from).toISOString());
    if (to) {
      const t = new Date(to);
      t.setHours(23, 59, 59, 999);
      p.set('to', t.toISOString());
    }
    if (q.trim()) p.set('q', q.trim());
    p.set('limit', String(limit));
    p.set('offset', String(offset));
    return p.toString();
  }, [status, from, to, q, limit, offset]);

  const fetchStories = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE_URL}/admin/fan-stories?${queryString}`, {
        method: 'GET',
        credentials: 'include',
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Failed to load fan stories');
        return;
      }
      const payload = data.data ?? data;
      setStories(payload?.stories ?? []);
      setTotal(payload?.total ?? 0);
    } catch (err) {
      setError(err?.message || 'Failed to load fan stories');
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => { fetchStories(); }, [fetchStories]);

  const openEdit = (story) => {
    setEditStory(story);
    setEditTitle(story.title ?? '');
    setEditStatus(story.status ?? 'candidate');
    setEditAdminHidden(!!story.admin_hidden);
  };

  const submitEdit = async () => {
    if (!editStory) return;
    setEditLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/admin/fan-stories/${editStory.internal_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({
          title: editTitle,
          status: editStatus,
          admin_hidden: editAdminHidden,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Update failed');
        return;
      }
      setSuccess('Story updated');
      setEditStory(null);
      await fetchStories();
    } catch (err) {
      setError(err?.message || 'Update failed');
    } finally {
      setEditLoading(false);
    }
  };

  const toggleHidden = async (story) => {
    try {
      const res = await fetch(`${BASE_URL}/admin/fan-stories/${story.internal_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ admin_hidden: !story.admin_hidden }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.message || 'Failed to toggle visibility');
        return;
      }
      setSuccess(`Story ${story.admin_hidden ? 'shown' : 'hidden'}`);
      await fetchStories();
    } catch (err) {
      setError(err?.message || 'Failed to toggle visibility');
    }
  };

  const deleteStory = async (story) => {
    if (!window.confirm(`Delete fan story "${story.title}"?`)) return;
    try {
      const res = await fetch(`${BASE_URL}/admin/fan-stories/${story.internal_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.message || 'Delete failed');
        return;
      }
      setSuccess('Story deleted');
      await fetchStories();
    } catch (err) {
      setError(err?.message || 'Delete failed');
    }
  };

  const openDetail = async (story) => {
    setDetailStory(story);
    setDetailPosts([]);
    setDetailLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/admin/fan-stories/${story.internal_id}`, {
        method: 'GET',
        credentials: 'include',
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const payload = data.data ?? data;
        setDetailPosts(payload?.posts ?? []);
        if (payload?.story) setDetailStory(payload.story);
      } else {
        setError(data.message || 'Failed to load story posts');
      }
    } catch (err) {
      setError(err?.message || 'Failed to load story posts');
    } finally {
      setDetailLoading(false);
    }
  };

  const removePostFromStory = async (feedId) => {
    if (!detailStory) return;
    if (!window.confirm('Remove this post from the story?')) return;
    try {
      const res = await fetch(`${BASE_URL}/admin/fan-stories/${detailStory.internal_id}/posts/${feedId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.message || 'Remove post failed');
        return;
      }
      setDetailPosts((prev) => prev.filter((p) => p.feed_id !== feedId));
      setSuccess('Post removed from story');
      fetchStories();
    } catch (err) {
      setError(err?.message || 'Remove post failed');
    }
  };

  const refreshStory = async (story) => {
    try {
      const res = await fetch(`${BASE_URL}/admin/fan-stories/${story.internal_id}/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.message || 'Refresh failed');
        return;
      }
      setSuccess('Refresh triggered');
      fetchStories();
    } catch (err) {
      setError(err?.message || 'Refresh failed');
    }
  };

  const resetFilters = () => {
    setStatus(''); setFrom(''); setTo(''); setQ(''); setOffset(0);
  };

  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-6"><h3>Fan Stories</h3></div>
              <div className="col-sm-6" style={{ textAlign: 'right' }}>
                <Button variant="outlined" size="small" onClick={fetchStories} disabled={loading}>
                  {loading ? <CircularProgress size={18} /> : 'Refresh'}
                </Button>
              </div>
            </div>
          </div>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }}>
                      {STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value || 'any'} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
                    value={from} onChange={(e) => { setFrom(e.target.value); setOffset(0); }} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
                    value={to} onChange={(e) => { setTo(e.target.value); setOffset(0); }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField label="Search title/summary" size="small" fullWidth
                    value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button variant="outlined" size="small" onClick={fetchStories} disabled={loading}>Apply</Button>
                    <Button variant="text" size="small" onClick={resetFilters}>Reset</Button>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      {total} total
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Match / Tournament</TableCell>
                  <TableCell>Players</TableCell>
                  <TableCell>Posts / Creators</TableCell>
                  <TableCell>Tone / Sentiment</TableCell>
                  <TableCell>Visibility</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last post</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stories.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="text.secondary">No fan stories match the current filters.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {stories.map((s) => (
                  <TableRow key={s.internal_id} hover>
                    <TableCell>
                      <Box sx={{ cursor: 'pointer' }} onClick={() => openDetail(s)}>
                        <Typography variant="body2" fontWeight={600}>{s.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.story_id}</Typography>
                        {s.summary && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            {s.summary}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={s.status} color={STATUS_COLOR[s.status] || 'default'} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{s.match || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.tournament || '—'}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{fmtTags(s.players)}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2">{s.posts_count} / {s.unique_creators_count}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">{s.dominant_tone || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.dominant_sentiment || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {s.admin_hidden
                        ? <Chip size="small" icon={<VisibilityOffIcon />} label="Hidden" color="warning" />
                        : <Chip size="small" icon={<VisibilityIcon />} label="Visible" color="success" />}
                    </TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(s.created_at)}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(s.last_post_at)}</Typography></TableCell>
                    <TableCell align="right">
                      <Tooltip title="View posts" arrow>
                        <Button size="small" variant="text" onClick={() => openDetail(s)}>View</Button>
                      </Tooltip>
                      <Tooltip title="Edit title / status" arrow>
                        <Button size="small" variant="text" onClick={() => openEdit(s)}><EditOutlinedIcon fontSize="small" /></Button>
                      </Tooltip>
                      <Tooltip title={s.admin_hidden ? 'Unhide story' : 'Hide story'} arrow>
                        <Button size="small" variant="text" onClick={() => toggleHidden(s)}>
                          {s.admin_hidden ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                        </Button>
                      </Tooltip>
                      <Tooltip title="Force LLM refresh" arrow>
                        <Button size="small" variant="text" onClick={() => refreshStory(s)}><RefreshIcon fontSize="small" /></Button>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <Button size="small" variant="text" color="error" onClick={() => deleteStory(s)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button size="small" variant="outlined" disabled={!canPrev} onClick={() => setOffset(Math.max(0, offset - limit))}>Prev</Button>
            <Typography variant="caption" sx={{ alignSelf: 'center' }}>{offset + 1}–{Math.min(offset + limit, total)} of {total}</Typography>
            <Button size="small" variant="outlined" disabled={!canNext} onClick={() => setOffset(offset + limit)}>Next</Button>
          </Box>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editStory} onClose={() => setEditStory(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit fan story</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Title" fullWidth value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                {STATUS_OPTIONS.filter((s) => s.value).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Visibility</InputLabel>
              <Select label="Visibility" value={editAdminHidden ? '1' : '0'} onChange={(e) => setEditAdminHidden(e.target.value === '1')}>
                <MenuItem value="0">Visible</MenuItem>
                <MenuItem value="1">Hidden</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditStory(null)} disabled={editLoading}>Cancel</Button>
          <Button variant="contained" onClick={submitEdit} disabled={editLoading || !editTitle.trim()}>
            {editLoading ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Posts detail dialog */}
      <Dialog open={!!detailStory} onClose={() => setDetailStory(null)} fullWidth maxWidth="md">
        <DialogTitle>
          {detailStory?.title}
          {detailStory && (
            <Typography variant="caption" display="block" color="text.secondary">
              {detailStory.story_id} · {detailStory.status} · posts {detailStory.posts_count} / creators {detailStory.unique_creators_count}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : detailPosts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No posts attached.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Author</TableCell>
                    <TableCell>Post</TableCell>
                    <TableCell>Tone / Sentiment</TableCell>
                    <TableCell>Quality</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailPosts.map((p, idx) => (
                    <TableRow key={p.feed_id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <Typography variant="caption">{p.author?.full_name || p.author?.username || `user_${p.user_id}`}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{p.content || '—'}</Typography>
                        {p.ai?.summary && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            AI: {p.ai.summary}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">{p.ai?.tone || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.ai?.sentiment || '—'}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="caption">{p.quality_score ?? '—'}</Typography></TableCell>
                      <TableCell align="right">
                        <Button size="small" color="error" onClick={() => removePostFromStory(p.feed_id)}>Remove</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailStory(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Component>
  );
}

export default FanStoriesAdmin;
